"use client";
import { cn } from "@/lib/utils";
import { CaretDownIcon } from "@radix-ui/react-icons";
import React, {
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ComboBox,
  type ComboBoxOptionType,
  DropdownInput,
  type DropdownOptionType,
} from ".";
import BackgroundColorSwatch, {
  useBackgroundDisplayInfo,
} from "../BackgroundColorSwatch";
import ColorSwatch from "./ColorSwatch";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from "../../../../ui/context-menu";
import { VariantControl } from "../variant/VariantControl";
import { ComboboxContainer } from ".";
import CustomColorPicker from "./CustomColorPicker";
import { validHex } from "./color-picker/utils/validate";
import {
  PropertyType,
  checkArbitraryTailwindClass,
  detectClassPropertyType,
  hasBackgroundClasses,
  hasBackgroundImageClasses,
  isSolidBackgroundClass,
} from "@/tailwind/tailwindUtils";
import { calculateNewClasses } from "../../utils";
import { UpdateClassesChange } from "@/store/zustand/changeLedgerTypes";
import { StylingModalType } from "../modals";
import { SquareDashedIcon } from "../icons";

import type { NewPropertyState } from "@/components/RightStyle/usePropertyStateV2";
import {
  parseColorValue,
  cleanBgColorValue,
  extractColorFromCssProperty,
} from "./color-picker/utils/colorFormatUtils";
import type { CssFieldValue } from "@/components/RightStyle";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { resolveColor } from "@/components/DesignTokens/utils/colorUtils";
import { useCanvasId } from "@/utils/utils";

interface ColorInputProps extends React.HTMLAttributes<HTMLDivElement> {
  alwaysShowHex?: boolean;
  // Property configuration
  propertyType: PropertyType;
  propertyState: Partial<NewPropertyState> & {
    propertyValue: string;
    displayValue: string;
    isSet: boolean;
    valueByVariant?: Record<
      string,
      { value: any; displayValue: string | null; active: boolean }
    >;
    setTemporaryDraggingValue?: (value: string | null) => void;
  };

  // Options and handlers
  comboboxOptions: ComboBoxOptionType[];
  setValue: (option: ComboBoxOptionType) => void;
  instantUpdateValue: (option: ComboBoxOptionType) => void;
  buildArbitraryClass: (input: string) => ComboBoxOptionType;
  pickTailwindClassForProperty?: (params: {
    option: ComboBoxOptionType;
    propertyType: PropertyType;
    setTemporaryDraggingValue?: (value: string | null) => void;
    variant?: string | null;
  }) => void;
  clearTailwindClassForProperty?: (params: {
    propertyType: PropertyType;
    setTemporaryDraggingValue?: (value: string | null) => void;
    variant?: string | null;
  }) => void;
  updateInstantForProperty?: (params: any) => void;

  // Element context
  elementClasses?: string[];
  selectedCodebaseId?: string;
  cssValues?: { [cssName: string]: CssFieldValue };

  // UI configuration
  dropdownOptions?: DropdownOptionType[];
  onDropdownSelected?: (option: DropdownOptionType) => void;
  buildOptionPreview?: (option: ComboBoxOptionType) => ReactNode;
  onInputFocus?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
  disabled?: boolean;
  onClear: () => void;
  placeholder?: string;

  // Modal handling
  setModal?: (modal: any) => void;
  modalRef?: React.RefObject<HTMLDivElement>;
  modalType?: StylingModalType;

  // Sorting
  sortTailwindColorOptions?: (
    options: ComboBoxOptionType[],
  ) => ComboBoxOptionType[];

  // Variant handling
  enableVariants?: boolean;
  onBeforeSetValue?: () => void;
  onClearVariant?: () => void;
  forceUpdate?: boolean;
  externalValue?: string;
}

const ColorInput: React.FC<ColorInputProps> = ({
  alwaysShowHex = false,
  propertyType,
  propertyState,
  comboboxOptions,
  setValue,
  instantUpdateValue,
  buildArbitraryClass,
  pickTailwindClassForProperty,
  clearTailwindClassForProperty,
  updateInstantForProperty,
  elementClasses = [],
  selectedCodebaseId,
  cssValues,
  buildOptionPreview,
  onInputFocus,
  disabled = false,
  onClear,
  placeholder,
  setModal,
  modalRef,
  modalType = StylingModalType.Fill,
  sortTailwindColorOptions,
  dropdownOptions = [],
  onDropdownSelected,
  enableVariants = false,
  onBeforeSetValue,
  onClearVariant,
  forceUpdate = false,
  externalValue,
  ...restProps
}) => {
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const chevronButtonRef = useRef<HTMLButtonElement>(null);
  const contextMenuTriggerRef = useRef<HTMLDivElement>(null);
  const wasRightClickRef = useRef(false);
  const isComboBoxDropdownClickRef = useRef(false);
  const variantModalOpenRef = useRef(false);
  const comboboxSelectionMadeRef = useRef(false);

  const tailwindClassesMap = useCodebaseStore(
    (state) => state.tailwindClassesMap,
  );

  const canvasId = useCanvasId();
  const [inputValue, setInputValue] = useState(() => {
    if (externalValue !== undefined) {
      // When we have an external value (from color picker), try to show hex if alwaysShowHex is true
      if (alwaysShowHex) {
        const parsedColor = parseColorValue(externalValue, comboboxOptions);
        if (parsedColor.isValid && parsedColor.hex) {
          return parsedColor.hex;
        }
      }
      return externalValue;
    }

    if (!propertyState?.isSet) return "";

    const rawValue =
      propertyState?.displayValue || propertyState?.propertyValue;
    if (!rawValue) return "";

    // When alwaysShowHex is enabled, try to extract hex from Tailwind class
    if (alwaysShowHex && rawValue) {
      const parsedColor = parseColorValue(rawValue, comboboxOptions);
      if (parsedColor.isValid && parsedColor.hex) {
        return parsedColor.hex;
      }
    }

    const cleanedValue = cleanBgColorValue(rawValue);
    if (cleanedValue && cleanedValue !== rawValue) {
      return cleanedValue;
    }

    return rawValue;
  });

  React.useEffect(() => {
    variantModalOpenRef.current = variantModalOpen;
  }, [variantModalOpen]);

  // Handle external value updates (from color picker dragging)
  React.useEffect(() => {
    if (externalValue && !isUserInteracting) {
      // When external value changes, it means color picker is being dragged
      if (alwaysShowHex) {
        // Try to parse and show hex when alwaysShowHex is true
        const parsedColor = parseColorValue(externalValue, comboboxOptions);
        if (parsedColor.isValid && parsedColor.hex) {
          setInputValue(parsedColor.hex);
        }
      }
      setInputValue(externalValue);
      return;
    }

    // When external value is cleared (becomes undefined), reset user interaction state
    // so that property state updates can flow through
    if (externalValue === undefined && isUserInteracting) {
      setIsUserInteracting(false);
    }
  }, [externalValue, alwaysShowHex, comboboxOptions, isUserInteracting]);

  /**
   * Syncs the input value with the current property state, unless the user is actively interacting
   * or an external value (e.g. from a color picker drag) is present.
   *
   * - If the property is not set, tries to derive a background class from elementClasses.
   * - If the property is set and forceUpdate is true, sets the input value to the CSS value,
   *   optionally cleaning it to hex if alwaysShowHex is enabled.
   * - Does nothing if the user is interacting or an external value is present.
   */
  React.useEffect(() => {
    if (isUserInteracting) {
      return;
    }

    // Don't override external values (from dragging)
    if (externalValue !== undefined) {
      return;
    }

    // Don't set input value to default fallback colors when property isn't actually set
    if (!propertyState.isSet) {
      return;
    }

    if (!propertyState?.cssValue) {
      return;
    }

    if (forceUpdate) {
      // When alwaysShowHex is enabled, try to extract hex from the CSS value
      if (alwaysShowHex) {
        const cleanedValue = cleanBgColorValue(propertyState.cssValue);
        if (cleanedValue) {
          setInputValue(cleanedValue);
          return;
        }
      }
      setInputValue(propertyState.cssValue);
      return;
    }
    //  Check if we should prefer the display value over the CSS value
    // For non-alwaysShowHex mode, prefer Tailwind class names when available
    let valueToShow = propertyState.cssValue;

    if (alwaysShowHex) {
      // When alwaysShowHex is enabled, try to show hex value instead of CSS
      const cleanedValue = cleanBgColorValue(propertyState.cssValue);
      if (cleanedValue) {
        valueToShow = cleanedValue;
      }
    } else {
      // When alwaysShowHex is false, prefer Tailwind class names

      if (propertyState.className) {
        // Check if the className is an arbitrary hex value like bg-[#fff]
        const arbitraryHexMatch = checkArbitraryTailwindClass(
          propertyState.className,
          propertyType,
        );
        if (arbitraryHexMatch) {
          // Extract and display the clean hex value
          const hexValue = arbitraryHexMatch.propertyValue;
          if (validHex(hexValue)) {
            valueToShow = hexValue;
          } else {
            valueToShow = propertyState.className;
          }
        } else {
          valueToShow = propertyState.className;
        }
      }
    }
    setInputValue(valueToShow);
  }, [
    externalValue,
    propertyState?.cssValue,
    propertyState?.displayValue,
    propertyState?.className,
    propertyState?.propertyValue,
    propertyState.isSet,
    forceUpdate,
    alwaysShowHex,
    elementClasses.join(","),
  ]);

  const sortedComboboxOptions = useMemo(
    () =>
      sortTailwindColorOptions
        ? sortTailwindColorOptions(comboboxOptions)
        : comboboxOptions,
    [sortTailwindColorOptions, comboboxOptions],
  );

  const handleVariantChange = useCallback(
    (variant: string, value: string) => {
      let option = comboboxOptions.find((opt) => opt.tailwind === value);

      // If no exact match found, build an arbitrary class for custom values
      if (!option && value && pickTailwindClassForProperty) {
        option = buildArbitraryClass(value);
      }

      if (option && pickTailwindClassForProperty) {
        pickTailwindClassForProperty({
          option,
          propertyType,
          setTemporaryDraggingValue: propertyState.setTemporaryDraggingValue,
          variant,
        });
      }
    },
    [
      comboboxOptions,
      propertyType,
      buildArbitraryClass,
      pickTailwindClassForProperty,
      propertyState.setTemporaryDraggingValue,
    ],
  );

  const handleClearVariant = useCallback(() => {
    if (!selectedCodebaseId) return;

    if (!tailwindClassesMap) return;

    // Get all unique variants present in the current classes for this property type
    const variants = new Set<string | null>();

    for (const cls of elementClasses) {
      // Check if this class matches our property type
      const variant = cls.includes(":") ? cls.split(":")[0] : null; // null for default variant

      // Test if this class would be affected by our property type
      const testResult = calculateNewClasses({
        option: null,
        propertyType,
        oldClasses: [cls],
        tailwindClassesMap,
        variant,
      });

      // If the class was removed, it means it matches our property type
      if (testResult.length === 0) {
        variants.add(variant);
      }
    }

    // Clear each variant separately
    let newClasses = [...elementClasses];
    for (const variant of variants) {
      newClasses = calculateNewClasses({
        option: null,
        propertyType,
        oldClasses: newClasses,
        tailwindClassesMap,
        variant,
      });
    }

    if (newClasses.length !== elementClasses.length) {
      const updateClassesChange = new UpdateClassesChange({
        codebaseIdToUpdateClass: selectedCodebaseId || "",
        oldClasses: elementClasses,
        newClasses: newClasses,
        involvedClasses: [],
        isInstantChangeOnly: false,
      });
      useDesignCommandManager.getState().executeCommand(updateClassesChange);
    }

    // Call the onClearVariant callback if provided
    if (onClearVariant) {
      onClearVariant();
    }
  }, [
    selectedCodebaseId,
    elementClasses.join(","),
    propertyType,
    onClearVariant,
    tailwindClassesMap,
  ]);

  const handleSwatchClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (setModal && modalRef) {
        setModal({
          type: modalType,
          anchor: modalRef,
        });
      }
    },
    [setModal, modalRef, modalType],
  );

  const handleChevronClick = useCallback(() => {
    if (
      enableVariants &&
      contextMenuTriggerRef.current &&
      chevronButtonRef.current
    ) {
      const rect = chevronButtonRef.current.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      const event = new MouseEvent("contextmenu", {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX: x,
        clientY: y,
      });
      contextMenuTriggerRef.current.dispatchEvent(event);
    } else {
      setDropdownVisible(true);
    }
  }, [enableVariants]);

  const onInputChange = useCallback((value: string) => {
    setIsUserInteracting(true);
    setInputValue(value);
  }, []);

  const handleInputCommit = useCallback(
    (value: string) => {
      if (value) {
        // Check if the value has actually changed before making any updates
        let currentDisplayValue =
          propertyState.displayValue || propertyState.propertyValue || "";
        let inputValue = value;

        // For alwaysShowHex mode, we need to compare hex values
        if (alwaysShowHex && currentDisplayValue) {
          const currentParsed = parseColorValue(
            currentDisplayValue,
            comboboxOptions,
          );
          if (currentParsed.isValid && currentParsed.hex) {
            currentDisplayValue = currentParsed.hex;
          }
        }

        const isTailwindClass = isSolidBackgroundClass(value);

        if (alwaysShowHex || !isTailwindClass) {
          const inputParsed = parseColorValue(value, comboboxOptions);
          if (inputParsed.isValid && inputParsed.hex) {
            inputValue = inputParsed.hex;
          }
        }

        // If the values are the same, don't make any changes
        if (
          currentDisplayValue === inputValue ||
          currentDisplayValue === value ||
          propertyState.propertyValue === value
        ) {
          setComboboxOpen(false);
          return;
        }

        // First check if the value is already a valid Tailwind class from the combobox options to prevent

        const option = buildArbitraryClass(inputValue);
        if (isTailwindClass) {
          // Use the existing option directly
          if (pickTailwindClassForProperty) {
            pickTailwindClassForProperty({
              option: {
                tailwind: inputValue,
                css: parseColorValue(inputValue, comboboxOptions).hex,
              },
              propertyType,
              setTemporaryDraggingValue:
                propertyState.setTemporaryDraggingValue,
            });
          } else {
            setValue(option);
          }
        } else {
          // For arbitrary values, apply the change first
          if (pickTailwindClassForProperty) {
            pickTailwindClassForProperty({
              option,
              propertyType,
              setTemporaryDraggingValue:
                propertyState.setTemporaryDraggingValue,
            });
          } else {
            setValue(option);
          }

          // If user entered a hex value, display it as the clean hex instead of bg-[hex]
          const isHexValue = validHex(inputValue);
          if (isHexValue) {
            setInputValue(inputValue);
          }
        }
      }
      setComboboxOpen(false);
    },
    [
      propertyState.displayValue,
      propertyState.propertyValue,
      propertyState.setTemporaryDraggingValue,
      alwaysShowHex,
      buildArbitraryClass,
      comboboxOptions,
      pickTailwindClassForProperty,
      propertyType,
      setValue,
    ],
  );

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        if (comboboxOpen) {
          setComboboxOpen(false);
          event.stopPropagation();
          event.preventDefault();
        } else {
          setIsUserInteracting(false);
          // Revert to the original value based on alwaysShowHex setting
          let revertValue = "";
          if (propertyState.isSet) {
            if (alwaysShowHex && propertyState.cssValue) {
              // Extract hex from CSS value if alwaysShowHex is true
              const parsedColor = parseColorValue(
                propertyState.cssValue,
                comboboxOptions,
              );
              revertValue =
                parsedColor.isValid && parsedColor.hex
                  ? parsedColor.hex
                  : propertyState.cssValue;
            } else {
              revertValue =
                propertyState.displayValue || propertyState.propertyValue || "";
            }
          }
          setInputValue(revertValue);
          if (inputRef.current) inputRef.current.blur();
        }
      } else if (event.key === "Enter") {
        // Only handle Enter if combobox is closed or no option is selected
        // The combobox component handles Enter when an option is selected
        if (!comboboxOpen) {
          handleInputCommit(inputValue);
          setIsUserInteracting(false);
          if (inputRef.current) inputRef.current.blur();
          event.preventDefault();
        }
      }
    },
    [
      comboboxOpen,
      propertyState.isSet,
      propertyState.cssValue,
      propertyState.displayValue,
      propertyState.propertyValue,
      alwaysShowHex,
      comboboxOptions,
      handleInputCommit,
      inputValue,
    ],
  );

  const handleComboboxOptionSelection = useCallback(
    (option: ComboBoxOptionType) => {
      if (onBeforeSetValue) onBeforeSetValue();

      // Mark that combobox made a selection to prevent onBlur from interfering
      comboboxSelectionMadeRef.current = true;

      // timeout to ensure the input value is set after
      setTimeout(() => {
        setIsUserInteracting(false);

        // For non-arbitrary values or when alwaysShowHex is enabled, use existing logic
        if (alwaysShowHex) {
          // When alwaysShowHex is true, try to extract hex value
          let hexValue = "";
          if (option.css) {
            const extractedColor = extractColorFromCssProperty(option.css);
            if (extractedColor && extractedColor !== option.css) {
              hexValue = extractedColor;
            }
          }

          // If we couldn't extract from CSS, fallback to parseColorValue with tailwind
          if (!hexValue && option.tailwind) {
            const parsedColor = parseColorValue(
              option.tailwind,
              comboboxOptions,
            );
            if (parsedColor.isValid) {
              hexValue = parsedColor.hex;
            }
          }

          // Set the input value to hex if we have it, otherwise use the tailwind class
          if (hexValue) {
            setInputValue(hexValue);
          } else {
            setInputValue(option.tailwind);
          }
        }
      }, 0);

      if (pickTailwindClassForProperty) {
        pickTailwindClassForProperty({
          option,
          propertyType,
          setTemporaryDraggingValue: propertyState.setTemporaryDraggingValue,
        });
      } else {
        setValue(option);
      }

      setComboboxOpen(false);
      if (inputRef.current) inputRef.current.blur();
    },
    [propertyType, comboboxOptions, alwaysShowHex],
  );

  const handleDropdownOptionClick = useCallback(
    (option: DropdownOptionType) => {
      if (onBeforeSetValue) onBeforeSetValue();
      if (option.value === "clear" && onClear) {
        onClear();
      } else if (onDropdownSelected) {
        onDropdownSelected(option);

        // When a dropdown option is selected and alwaysShowHex is true, update input to show hex
        if (alwaysShowHex && option.value && typeof option.value === "string") {
          const colorOption = comboboxOptions.find(
            (opt) =>
              opt.tailwind === option.value ||
              opt.tailwind === `bg-${option.value}`,
          );
          if (colorOption) {
            // First try to extract from CSS property directly
            let hexValue = "";
            if (colorOption.css) {
              const extractedColor = extractColorFromCssProperty(
                colorOption.css,
              );
              if (extractedColor && extractedColor !== colorOption.css) {
                hexValue = extractedColor;
              }
            }

            // If we couldn't extract from CSS, fallback to parseColorValue with tailwind
            if (!hexValue && colorOption.tailwind) {
              const parsedColor = parseColorValue(
                colorOption.tailwind,
                comboboxOptions,
              );
              hexValue = parsedColor.hex;
            }

            if (hexValue) {
              setTimeout(() => {
                setInputValue(hexValue);
              }, 0);
            }
          }
        }
      }
      setDropdownVisible(false);
    },
    [alwaysShowHex, comboboxOptions],
  );

  const fallbackBuildOptionPreview = useCallback(
    (option: ComboBoxOptionType): ReactNode => {
      if (buildOptionPreview) {
        return buildOptionPreview(option);
      }

      let backgroundColor = "";
      if (option.css) {
        const cleanedValue = cleanBgColorValue(option.css);
        backgroundColor = cleanedValue || "";

        if (cleanedValue === option.css) {
          const colorMatch = option.css.match(/color:\s*([^;]+)/);
          if (colorMatch) {
            backgroundColor = colorMatch[1].trim();
          }
        }
      } else if (option.tailwind) {
        const parsedColor = parseColorValue(option.tailwind, comboboxOptions);
        if (parsedColor.isValid) {
          backgroundColor = parsedColor.hex;
        }
      }

      return (
        <ColorSwatch
          color={backgroundColor}
          size="sm"
          className="mr-2 inline-block"
        />
      );
    },
    [comboboxOptions],
  );

  // Check if we have a current color value (from input or dragging)
  const currentColorValue = externalValue || inputValue;
  const hasCurrentColor = currentColorValue && currentColorValue !== "";

  // Helper function to resolve colors from various sources
  const resolveCurrentColor = useCallback(() => {
    // First try the computed CSS values (highest priority)
    const computedColor =
      propertyType === PropertyType.TEXT_COLOR
        ? cssValues?.color?.evaluatedValue
        : propertyType === PropertyType.BORDER_COLOR
          ? cssValues?.["border-color"]?.evaluatedValue
          : cssValues?.["background-color"]?.evaluatedValue;

    if (computedColor) {
      return computedColor;
    }

    // Then try design token resolution, but only with classes that match our property type
    if (canvasId && elementClasses?.length) {
      // Filter classes to only include those that match our property type
      const relevantClasses = elementClasses.filter((cls) => {
        switch (propertyType) {
          case PropertyType.BACKGROUND_COLOR:
            return cls.startsWith("bg-") && !cls.startsWith("dark:");
          case PropertyType.TEXT_COLOR:
            return cls.startsWith("text-") && !cls.startsWith("dark:");
          case PropertyType.BORDER_COLOR: {
            // Include the generic 'border' class
            if (cls === "border") return true;

            // Only include border color classes, not width/directional classes
            if (!cls.startsWith("border-") || cls.startsWith("dark:")) {
              return false;
            }

            // For arbitrary classes, use detectClassPropertyType to check if this is actually a border color
            if (cls.includes("[")) {
              const detectedType = detectClassPropertyType(cls);
              if (detectedType === PropertyType.BORDER_WIDTH) {
                return false;
              }
            }

            // Also exclude directional border classes
            if (cls.match(/^border-(t-|b-|l-|r-|x-|y-)/)) {
              return false;
            }

            return true;
          }
          default:
            return false;
        }
      });

      if (relevantClasses.length > 0) {
        // For arbitrary classes, try to extract hex directly first
        const arbitraryClass = relevantClasses.find((cls) => cls.includes("["));
        if (arbitraryClass) {
          const hexMatch = arbitraryClass.match(/\[([#][a-fA-F0-9]+)\]/);
          if (hexMatch) {
            return hexMatch[1];
          }
        }

        const designTokenColor = resolveColor(canvasId, relevantClasses);
        if (designTokenColor) {
          return designTokenColor;
        }
      }
    }

    // Then try property state cssValue
    if (propertyState.cssValue) {
      return propertyState.cssValue;
    }

    // Then try current input/dragging value
    if (currentColorValue) {
      return currentColorValue;
    }

    return null;
  }, [
    propertyType,
    cssValues,
    canvasId,
    elementClasses,
    propertyState.cssValue,
    currentColorValue,
  ]);

  // Use the shared hook to determine if background swatch needs full width
  const { needsFullWidth: backgroundNeedsFullWidth } =
    useBackgroundDisplayInfo(cssValues);

  const inputElement = (
    <div
      ref={contextMenuTriggerRef}
      onMouseDown={(e) => {
        if (e.button === 2) {
          e.preventDefault();
          wasRightClickRef.current = true;
        }
      }}
      className="items-center mb-2"
      {...restProps}
    >
      <div className="flex flex-row items-center relative border bg-secondary-n-950 rounded-md h-8 px-2 hover:border-secondary-600 border-transparent min-w-0">
        <div
          ref={modalRef}
          className={cn(
            "mr-2 vertical-center cursor-pointer",
            backgroundNeedsFullWidth && !alwaysShowHex ? "w-full" : null,
          )}
          onClick={handleSwatchClick}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            {!hasCurrentColor ? (
              <SquareDashedIcon
                strokeWidth={1}
                className="text-secondary-n-600 w-4 h-4"
              />
            ) : alwaysShowHex ||
              propertyType !== PropertyType.BACKGROUND_COLOR ? (
              <ColorSwatch color={resolveCurrentColor()} size="sm" />
            ) : (
              <BackgroundColorSwatch
                size="sm"
                selectedCodebaseId={selectedCodebaseId}
                classList={elementClasses}
                cssValues={cssValues}
                elementClasses={elementClasses}
              />
            )}
          </div>
        </div>
        <div className="flex-1 min-w-0 relative">
          <input
            ref={inputRef}
            disabled={disabled}
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            onFocus={(event) => {
              setIsUserInteracting(true);
              if (!wasRightClickRef.current && !variantModalOpenRef.current) {
                setComboboxOpen(true);
              }
              wasRightClickRef.current = false;
              event.target.select();
              if (onInputFocus) {
                onInputFocus(event);
              }
            }}
            onMouseDown={(e) => {
              if (e.button === 2) {
                wasRightClickRef.current = true;
                e.preventDefault();
              } else if (e.button === 0 && !variantModalOpenRef.current) {
                setTimeout(() => {
                  setComboboxOpen(true);
                }, 10);
              }
            }}
            onKeyDown={handleInputKeyDown}
            onBlur={() => {
              if (isComboBoxDropdownClickRef.current) {
                return;
              }

              // If combobox just made a selection, don't commit the raw input
              if (comboboxSelectionMadeRef.current) {
                comboboxSelectionMadeRef.current = false;
                setIsUserInteracting(false);
                return;
              }

              setIsUserInteracting(false);
              handleInputCommit(inputValue);
            }}
            className="text-xs font-medium bg-transparent text-secondary-n-50 outline-hidden border-none w-full min-w-0"
            style={{
              minWidth: 0,
              maxWidth: "calc(100% - 20px)",
            }}
            placeholder={placeholder || "Add fill"}
            spellCheck={false}
            autoComplete="off"
            aria-label={`${propertyType} color`}
          />

          {enableVariants && (
            <button
              type="button"
              className="absolute -right-[4px] top-1/2 -translate-y-1/2 flex z-2 items-center justify-center p-0.5"
              onClick={handleChevronClick}
              tabIndex={0}
              aria-label={
                enableVariants
                  ? "Open variant and breakpoint menu"
                  : "Open options"
              }
              ref={chevronButtonRef}
            >
              <CaretDownIcon />
            </button>
          )}

          {!enableVariants && (
            <DropdownInput
              className="w-40"
              dropdownOptions={dropdownOptions}
              onDropdownSelected={handleDropdownOptionClick}
              visible={Boolean(dropdownVisible)}
              setVisible={setDropdownVisible}
            />
          )}

          {comboboxOpen && (
            <ComboBox
              input={inputValue}
              options={sortedComboboxOptions}
              open={comboboxOpen}
              closeCombobox={() => setComboboxOpen(false)}
              onOptionSelected={handleComboboxOptionSelection}
              buildOptionPreview={fallbackBuildOptionPreview}
              buildArbitraryClass={(input) => {
                const isTailwindClass = isSolidBackgroundClass(input);
                if (isTailwindClass) {
                  return buildArbitraryClass(input);
                }
                return buildArbitraryClass(
                  parseColorValue(input, comboboxOptions)?.hex ?? input,
                );
              }}
              anchorRef={inputRef}
              onMouseDown={() => {
                isComboBoxDropdownClickRef.current = true;
              }}
              onMouseUp={() => {
                setTimeout(() => {
                  isComboBoxDropdownClickRef.current = false;
                }, 0);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );

  if (!enableVariants || !propertyState.valueByVariant) {
    return inputElement;
  }

  return (
    <ContextMenu onOpenChange={setVariantModalOpen}>
      <ContextMenuTrigger asChild>{inputElement}</ContextMenuTrigger>
      <ContextMenuContent
        className="p-0 border-none"
        // Stop propagation to prevent the context menu from being closed before the events are handled
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <VariantControl
          valueByVariant={propertyState.valueByVariant}
          onVariantChange={handleVariantChange}
          onClearVariant={handleClearVariant}
          controlType="text-color"
          anchorRef={inputRef}
          currentFlexDirection=""
          currentJustifyContent=""
          currentAlignItems=""
        >
          {(variant, expanded) => {
            if (!expanded || !variant) return null;
            const variantValue =
              propertyState.valueByVariant?.[variant]?.value || "";
            const variantDisplayValue =
              propertyState.valueByVariant?.[variant]?.displayValue || "";
            const variantIsSet =
              !!propertyState.valueByVariant?.[variant]?.active;

            let hexForPicker = variantValue;
            if (validHex(variantDisplayValue)) {
              hexForPicker = variantDisplayValue;
            } else {
              // Extract hex from arbitrary value based on property type
              const prefix =
                propertyType === PropertyType.BACKGROUND_COLOR
                  ? "bg-"
                  : propertyType === PropertyType.TEXT_COLOR
                    ? "text-"
                    : propertyType === PropertyType.BORDER_COLOR
                      ? "border-"
                      : "bg-";
              const arbitraryMatch = variantValue.match(
                new RegExp(`^${prefix}\\[([#a-fA-F0-9]+)\\]$`),
              );
              if (arbitraryMatch) {
                hexForPicker = arbitraryMatch[1].startsWith("#")
                  ? arbitraryMatch[1]
                  : `#${arbitraryMatch[1]}`;
              }
            }

            const variantPropertyState = {
              ...propertyState,
              propertyValue: hexForPicker,
              displayValue: variantDisplayValue,
              isSet: variantIsSet,
            };

            return (
              <ComboboxContainer>
                <CustomColorPicker
                  propertyState={variantPropertyState}
                  buildArbitraryClass={buildArbitraryClass}
                  instantUpdateValue={(option) => {
                    if (updateInstantForProperty) {
                      updateInstantForProperty({
                        propertyType,
                        option,
                        setTemporaryDraggingValue:
                          propertyState.setTemporaryDraggingValue,
                        variant,
                      });
                    }
                  }}
                  comboboxOptions={
                    sortTailwindColorOptions
                      ? sortTailwindColorOptions(comboboxOptions)
                      : comboboxOptions
                  }
                  setValue={(option) => {
                    if (pickTailwindClassForProperty) {
                      pickTailwindClassForProperty({
                        option,
                        propertyType,
                        setTemporaryDraggingValue:
                          propertyState.setTemporaryDraggingValue,
                        variant,
                      });
                    }
                  }}
                  tooltipContent={`${propertyType} color`}
                  onClear={() => {
                    if (clearTailwindClassForProperty) {
                      clearTailwindClassForProperty({
                        propertyType,
                        setTemporaryDraggingValue:
                          propertyState.setTemporaryDraggingValue,
                        variant,
                      });
                    }
                  }}
                  showHex={true}
                />
              </ComboboxContainer>
            );
          }}
        </VariantControl>
      </ContextMenuContent>
    </ContextMenu>
  );
};

export default React.memo(ColorInput);
